import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import api from "../api/client";
import type { AddFormState, BankProviderEntry, BankSettingsValue, EditFormState } from "../types/settings";
import { DEFAULT_BANK_TYPE, FormMode } from "../types/settings";

const BankSettingsContext = createContext<BankSettingsValue | undefined>(undefined);

const BankSettingsProvider = ({ children }: { children: ReactNode }) => {
  const [banks, setBanks] = useState<BankProviderEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [formMode, setFormMode] = useState<FormMode>(FormMode.IDLE);
  const [editingId, setEditingId] = useState<number | null>(null);

  const [addForm, setAddForm] = useState<AddFormState>({ name: "", type: DEFAULT_BANK_TYPE, username: "", password: "" });
  const [editForm, setEditForm] = useState<EditFormState>({ name: "", username: "", password: "" });
  const patchAddForm = (partial: Partial<AddFormState>) => setAddForm((prev) => ({ ...prev, ...partial }));
  const patchEditForm = (partial: Partial<EditFormState>) => setEditForm((prev) => ({ ...prev, ...partial }));

  const loadBanks = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await api.banks.list();
      setBanks(data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to load banks");
    } finally {
      setLoading(false);
    }
  };

  const handleAddBank = async () => {
    try {
      if (!addForm.name.trim()) throw new Error("Bank name is required");
      await api.banks.create({
        name: addForm.name.trim(),
        type: addForm.type,
        username: addForm.username || undefined,
        password: addForm.password || undefined,
      });
      setAddForm({ name: "", type: DEFAULT_BANK_TYPE, username: "", password: "" });
      setFormMode(FormMode.IDLE);
      await loadBanks();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to create bank");
    }
  };

  const handleUpdateBank = async (id: number, name?: string, username?: string | null, password?: string) => {
    try {
      if (!name?.trim()) throw new Error("Bank name is required");
      await api.banks.update(id, { name: name.trim(), ...(username && { username }), ...(password && { password }) });
      setEditingId(null);
      setEditForm({ name: "", username: "", password: "" });
      await loadBanks();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to update bank");
    }
  };

  const handleSetDefault = async (id: number) => {
    try {
      await api.banks.setDefault(id);
      await loadBanks();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to set default");
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await api.banks.remove(id);
      if (editingId === id) {
        setEditingId(null);
        setEditForm({ name: "", username: "", password: "" });
      }
      await loadBanks();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to delete bank");
    }
  };

  const startEdit = (bank: BankProviderEntry) => {
    if (formMode === FormMode.ADD) {
      setAddForm({ name: "", type: DEFAULT_BANK_TYPE, username: "", password: "" });
    }
    setEditingId(bank.id);
    setEditForm({ name: bank.name, username: bank.username ?? "", password: bank.password ?? "" });
    setFormMode(FormMode.EDIT);
  };

  const stopEditing = () => {
    setEditingId(null);
    setEditForm({ name: "", username: "", password: "" });
  };

  const startAdding = () => {
    if (formMode === FormMode.EDIT) stopEditing();
    setAddForm({ name: "", type: DEFAULT_BANK_TYPE, username: "", password: "" });
    setFormMode(FormMode.ADD);
  };

  const cancelAdding = () => {
    setAddForm({ name: "", type: DEFAULT_BANK_TYPE, username: "", password: "" });
    setFormMode(FormMode.IDLE);
  };

  useEffect(() => {
    loadBanks();
  }, []);

  return (
    <BankSettingsContext.Provider
      value={{
        banks,
        loading,
        error,
        formMode,
        editingId,
        addForm,
        patchAddForm,
        editForm,
        patchEditForm,
        loadBanks,
        handleAddBank,
        handleUpdateBank,
        handleSetDefault,
        handleDelete,
        startEdit,
        stopEditing,
        startAdding,
        cancelAdding,
      }}
    >
      {children}
    </BankSettingsContext.Provider>
  );
};

const useBankSettings = () => {
  const context = useContext(BankSettingsContext);
  if (context === undefined) throw new Error("useBankSettings must be used within BankSettingsProvider");
  return context;
};

export { BankSettingsProvider, useBankSettings };
